// Strict structural equality for JSON-shaped values.
//
// The ONE deep-equal in the codebase. Both known callers are
// "append-on-change" diff gates — they ask *did this value change vs the
// last stored snapshot?* to decide whether to append a history entry:
//   - profiles.ts   → Profile.painMap  → painHistory
//   - programs.ts   → Program.targets  → target history (CO-7)
// Two hand-rolled copies of that question could disagree about what
// counts as "changed", which is the worst kind of latent bug: a history
// that silently misses an edit. Hence one implementation.
//
// Scope: plain JSON shapes — primitives, arrays, and plain objects. NOT
// Date/Map/Set/RegExp/class instances (no caller needs them; they'd
// compare as bare objects and quietly report equal). Keep it that way,
// or teach it those types explicitly rather than letting a caller
// discover the gap at runtime.
//
// STRICT about undefined: `deepEqual(undefined, {})` is FALSE. A caller
// wanting the looser "absent and empty both mean no opinion" reading
// must normalise at its own site (`deepEqual(a ?? {}, b ?? {})`) — that
// choice belongs to the caller's domain, not baked in here where it
// would silently apply to callers that do distinguish the two.
export const deepEqual = (a: unknown, b: unknown): boolean => {
  // Object.is over === so NaN equals NaN and +0/-0 stay distinct. JSON
  // carries neither, but a caller diffing in-memory values might.
  if (Object.is(a, b)) return true;

  // Past this point a null/undefined can only mismatch: Object.is above
  // already accepted the equal-and-nullish cases.
  if (a === null || b === null || a === undefined || b === undefined) {
    return false;
  }
  if (typeof a !== "object" || typeof b !== "object") return false;

  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return false;

  if (aIsArray) {
    const aArr = a as unknown[];
    const bArr = b as unknown[];
    if (aArr.length !== bArr.length) return false;
    return aArr.every((item, i) => deepEqual(item, bArr[i]));
  }

  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj);
  // Key-count reject first: cheap, and it makes the per-key loop below
  // sufficient (a key in b but not in a would change the count).
  if (aKeys.length !== Object.keys(bObj).length) return false;
  return aKeys.every(
    (k) => Object.hasOwn(bObj, k) && deepEqual(aObj[k], bObj[k]),
  );
};
